import { line } from "@antv/g2plot";
import { right } from "@antv/g2plot/lib/plots/sankey/sankey";
import { throttle } from "@antv/util";
import { default as EscPosEncoder } from "@manhnd/esc-pos-encoder";
let customEncoder = new EscPosEncoder();

const MAX_CHUNK_SIZE = 512; // Bluetooth MTU Limit
// ESC/POS Commands
const ESC = "\x1B"; // Escape character
const GS = "\x1D"; // Group Separator

// Printer Initialization
const INITIALIZE_PRINTER = `${ESC}@`; // Reset printer to default

// Text Alignment
const ALIGN_LEFT = `${ESC}a\x00`; // Left align text
const ALIGN_CENTER = `${ESC}a\x01`; // Center align text
const ALIGN_RIGHT = `${ESC}a\x02`; // Right align text

// Text Formatting
const BOLD_ON = `${ESC}E\x01`; // Bold text on
const BOLD_OFF = `${ESC}E\x00`; // Bold text off
const NORMAL_HEIGHT_WIDTH = `${GS}!\x00`; // Normal size
const DOUBLE_HEIGHT = `${GS}!\x01`; // Double height
const DOUBLE_WIDTH = `${GS}!\x02`; // Double width
const DOUBLE_HEIGHT_WIDTH = `${GS}!\x03`; // Double height and width
const DOUBLE_HEIGHT_WIDTH_BOLD = `${GS}!\x03${ESC}E\x01`; // Double height, width, and bold

// Line Spacing
const DEFAULT_LINE_SPACING = `${ESC}\x32`; // Default line spacing
const CUSTOM_LINE_SPACING = (dots) => `${ESC}\x33${String.fromCharCode(dots)}`;

// Paper Cutting
const PARTIAL_CUT = `${GS}\x56\x01`; // Partial cut
const FULL_CUT = `${GS}\x56\x00`; // Full cut

export default async function printerReceipt(
  characteristic,
  receiptDetails,
  transactionInfo,
  plates,
  fetchWrapper
) {
  try {
    if (receiptDetails.connection_type == "Bluetooth") {
      if (!characteristic)
        throw new Error("Bluetooth characteristic is not available");

      if (!receiptDetails || !transactionInfo || !plates || plates.length === 0)
        throw new Error("Invalid receipt data provided");

      const plateItems = plates[0].items;
      const columnWidth = receiptDetails.column_width;
      const LINE_SEPARATOR = "-".repeat(columnWidth) + "\n";

      const receiptContent = [
        INITIALIZE_PRINTER,
        `${ALIGN_CENTER}${DOUBLE_HEIGHT_WIDTH_BOLD}`,
        `${receiptDetails.merchant_name}\n`,
        `${NORMAL_HEIGHT_WIDTH}${BOLD_OFF}${receiptDetails.custom_message}\n`,
        LINE_SEPARATOR,
        `${BOLD_ON}Invoice No: ${transactionInfo.invoice_no}\n${BOLD_OFF}`,
        `${transactionInfo.transaction_datetime}\n`,
        `${LINE_SEPARATOR}${formatAlignedLine(
          "Qty Items",
          "Price",
          columnWidth
        )}${LINE_SEPARATOR}`,
        ALIGN_LEFT,
        formatPlateItems(plateItems, columnWidth),
        LINE_SEPARATOR,
        formatAlignedLine(
          "Subtotal:",
          `RM${transactionInfo.subtotal.toFixed(2)}`,
          columnWidth
        ),
        formatAlignedLine(
          `SST (${receiptDetails.sst_rate}%):`,
          `RM${transactionInfo.sst_amount.toFixed(2)}`,
          columnWidth
        ),
        transactionInfo.round_adj_amount &&
          formatAlignedLine(
            "Round Adj:",
            `RM${transactionInfo.round_adj_amount.toFixed(2)}`,
            columnWidth
          ),
        `${BOLD_ON}${formatAlignedLine(
          "Total:",
          `RM${transactionInfo.amount.toFixed(2)}`,
          columnWidth
        )}`,
        `${formatAlignedLine(
          "Payment Method:",
          `${transactionInfo.payment_method}`,
          columnWidth
        )}`,
        `${BOLD_OFF}${formatAlignedLine(
          "Pay Amount:",
          `RM${transactionInfo.pay_amount.toFixed(2)}`,
          columnWidth
        )}`,
        formatAlignedLine(
          "Change:",
          `RM${transactionInfo.change_amount.toFixed(2)}`,
          columnWidth
        ),
        ALIGN_CENTER,
        "\nSupported By\n",
      ]
        .filter(Boolean)
        .join("");

      // Convert the receipt content to a Uint8Array (raw bytes)
      const textEncoder = new TextEncoder();
      const receiptBytes = textEncoder.encode(receiptContent);

      // Send data in chunks to avoid exceeding the 512-byte limit
      await sendChunks(characteristic, receiptBytes, MAX_CHUNK_SIZE);

      console.log("Printing image logo");
      // Process and print logo image
      if (receiptDetails.support_logo) {
        await printLogoImage(characteristic, receiptDetails.support_logo);
      }

      console.log("Printing empty line");
      // Add empty lines and cut paper
      const emptyLines = "\n" + PARTIAL_CUT;
      await sendChunks(
        characteristic,
        textEncoder.encode(emptyLines),
        MAX_CHUNK_SIZE
      );
    } else if (receiptDetails.connection_type == "LPT") {
      await lptPrint(plates, receiptDetails, transactionInfo, fetchWrapper);
    } else {
      console.log("Printer connection type not supported.");
    }

    // If everything is done, return success
    return { status: true };
  } catch (error) {
    return { status: false, message: error };
  }
}

// 🖼️ Print Logo Image
async function printLogoImage(characteristic, imageUrl) {
  try {
    const img = new Image();
    img.src = imageUrl;

    await new Promise((resolve, reject) => {
      img.onload = async function () {
        try {
          const originalWidth = img.width;
          const originalHeight = img.height;

          if (!originalWidth || !originalHeight) {
            throw new Error("Invalid image dimensions.");
          }

          const newWidth = Math.floor(originalWidth / 8) * 8;
          const aspectRatio = originalHeight / originalWidth;
          const newHeight = Math.floor((newWidth * aspectRatio) / 8) * 8;

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = newWidth;
          canvas.height = newHeight;
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          const result = customEncoder
            .image(canvas, newWidth, newHeight, "atkinson")
            .encode();
          await sendChunks(characteristic, result, MAX_CHUNK_SIZE);
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = function () {
        reject(new Error("Failed to load image."));
      };
    });
  } catch (error) {
    console.error("Image Processing Error:", error.message);
  }
}

// 📝 Format text alignment
function formatAlignedLine(leftText, rightText, columnWidth) {
  try {
    if (leftText.length + rightText.length > columnWidth) {
      throw new Error("Text exceeds the printer's column width.");
    }
    const spaces = " ".repeat(columnWidth - leftText.length - rightText.length);
    return leftText + spaces + rightText + "\n";
  } catch (error) {
    console.error("Text Formatting Error:", error.message);
    return leftText + " " + rightText + "\n";
  }
}

// 🍽️ Format plate items
function formatPlateItems(plateItems, columnWidth) {
  try {
    let itemList = "";
    let MINRIGHTSPACES = 5;

    for (let item of plateItems) {
      if (!item || !item.quantity || !item.category || !item.unit_price) {
        continue; // Skip invalid items
      }

      const leftSpace = item.quantity.toString().length === 2 ? " " : "  ";
      const quantityText = `${item.quantity}x`;
      const leftText = `${quantityText}${leftSpace}${item.category}`;
      const rightText = item.unit_price.toFixed(2);
      const itemText = formatAlignedLine(leftText, rightText, columnWidth);

      let subCategory = `- ${item.sub_category || "Unknown"}`;

      // Call the formatTextWithFixedSpaces with dynamic minRightSpaces
      let formattedSubCategory = formatTextWithSpaces(
        subCategory,
        columnWidth,
        MINRIGHTSPACES, // Pass the dynamic minRightSpaces here
        quantityText.length
      );

      itemList += itemText + formattedSubCategory + "\n";
    }

    return itemList;
  } catch (error) {
    console.error("Plate Item Formatting Error:", error.message);
    return "";
  }
}

function formatTextWithSpaces(
  text,
  columnWidth,
  minRightSpaces,
  initialIndent
) {
  let words = text.split(" ");
  let lines = [];
  let currentLine = " ".repeat(initialIndent); // Start with initial indent

  for (let word of words) {
    if ((currentLine + word).length + minRightSpaces > columnWidth) {
      // Push current line with required right spaces
      lines.push(currentLine + " ".repeat(minRightSpaces));
      // Start a new line with increased indent (quantityText length + 2 spaces)
      currentLine = " ".repeat(initialIndent + 2) + word;
    } else {
      currentLine += (currentLine ? " " : "") + word;
    }
  }

  // Push the last line with fixed spaces
  if (currentLine) {
    lines.push(currentLine + " ".repeat(minRightSpaces));
  }

  return lines.join(""); // Ensure each line has a newline
}

// ✅ Send data in chunks
async function sendChunks(characteristic, data, chunkSize) {
  try {
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      await characteristic.writeValueWithResponse(chunk);
    }
  } catch (error) {
    console.error("Bluetooth Write Error:", error.message);
    throw new Error("Failed to send data to the printer.");
  }
}

// Print receipt using LPT Port
async function lptPrint(plates, receiptDetails, transactionInfo, fetchWrapper) {
  return new Promise((resolve, reject) => {
    fetchWrapper({
      backend_url: "http://127.0.0.1:5051/",
      endpoint_url: "printer/printReceipt",
      params: {
        plates: plates,

        merchant_name: receiptDetails.merchant_name,
        supported_by_logo: receiptDetails.support_logo,
        SST_enabled: receiptDetails.sst_enabled,
        SST_rate: receiptDetails.sst_rate,

        custom_message: receiptDetails.custom_message,
        currency_symbol: receiptDetails.currency_symbol,
        port: receiptDetails.port,
        max_text_length: receiptDetails.column_width,
        printing_width: receiptDetails.printing_width,
        resolution: receiptDetails.resolution,
        quantity_length: receiptDetails.quantity_length,
        desc_length: receiptDetails.desc_length,
        price_length: receiptDetails.price_length,

        invoice_no: transactionInfo.invoice_no,
        transaction_datetime: transactionInfo.transaction_datetime,
        pg_transaction_id: transactionInfo.pg_transaction_id,
        amount: transactionInfo.amount,
        pay_amount: transactionInfo.pay_amount,
        payment_method: transactionInfo.payment_method,
        sst_amount: transactionInfo.sst_amount,
        change_amount: transactionInfo.change_amount,
        round_adj_amount: transactionInfo.round_adj_amount,
        subtotal_amount:
          transactionInfo.amount -
          transactionInfo.sst_amount -
          transactionInfo.round_adj_amount,
      },
      onSuccess: (fetch_output) => {
        if (!("error" in fetch_output)) {
          resolve();
        } else {
          reject("Internal error. Printer failed to process data.");
        }
      },
      onFetchError: () => {
        reject("Failed to send data to the printer.");
      },
      onResponseError: () => {
        reject("Failed to send data to the printer.");
      },
    });
  });
}
